<template>
  <div
    :class="[{ 'owc-stage--dark': isDarkMode }, isSimpleImageStage ? 'owc-image-stage' : 'owc-text-stage']"
    class="owc-stage"
    :data-theme="brand"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div class="owc-stage__image" v-if="isSimpleImageStage">
      <StageImage :imageData="personaliseableData" />
      <div class="owc-stage__gradient" :class="{ 'owc-stage__gradient--retail': retailLogo }"></div>
    </div>
    <div class="owc-stage__content-wrapper">
      <div class="owc-stage__content wb-grid-container">
        <div class="owc-stage__text wb-grid-row">
          <div
            class="owc-stage__text-column wb-grid-col-mq1-12 wb-grid-col-mq3-7"
            :class="[isSimpleImageStage ? 'wb-grid-col-mq4-5' : 'wb-grid-col-mq4-6']"
          >
            <img
              v-if="retailLogo && retailLogo.source"
              class="owc-stage__dealer-logo"
              :src="retailLogo.source"
              :alt="retailLogo.alt"
              :title="retailLogo.title"
            />
            <h1 class="owc-stage__title wb-type-heading-xl" v-html="personaliseableData.headline"></h1>
            <div
              v-if="personaliseableData.description"
              v-html="personaliseableData.description"
              class="owc-stage__description wb-type-copy"
            ></div>
            <StagePrice
              v-if="personaliseableData.pricing"
              :class="{ 'wb-price--theme-dark': isDarkMode }"
              :priceData="personaliseableData.pricing"
              :vehicleData="personaliseableData.vehicleData"
            ></StagePrice>
          </div>
        </div>
        <div v-if="isSimpleImageStage && !hasFeatureStageItems" class="owc-stage__cta-wrapper wb-grid-row">
          <div class="wb-grid-col-mq1-12 owc-stage__cta-column">
            <StageCtaButtons
              :isDarkMode="isDarkMode"
              :buttons="personaliseableData.buttons"
              :cta="personaliseableData.cta"
              :vehicleData="personaliseableData.vehicleData"
            ></StageCtaButtons>
          </div>
        </div>
        <FeatureStageSlider
          v-if="hasFeatureStageItems && isSimpleImageStage"
          :stageSliderData="personaliseableData"
        >
          <div class="owc-feature-stage">
            <StageCtaButtons
              v-if="isSimpleImageStage"
              :isDarkMode="isDarkMode"
              :buttons="personaliseableData.buttons"
              :cta="personaliseableData.cta"
              :vehicleData="personaliseableData.vehicleData"
            ></StageCtaButtons>
          </div>
        </FeatureStageSlider>
      </div>
    </div>
    <div v-if="footnotes" class="wb-grid-container owc-stage__footnotes">
      <globalelem-footnote-component
        :module-id="moduleId"
        :theme="isDarkMode ? 'dark' : 'light'"
      ></globalelem-footnote-component>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * `Stage` - The Stage of the page, also called Hero Section, is a large element that is pinned to the header section and usually has a prominent visual hierarchy because it is often the first thing users see when arriving on a website.
 * The simple stage can be active in two modes. First, the simple text stage. This is the less complex state where only a headline, text and an optional dealer-logo is displayed.
 * Second, the simple image stage. A far more content-rich mode where _additionally_ two CTA buttons, a background image, image-gradients and footnotes are featured.
 */
import { StageComponent } from './Stage';
import StageCtaButtons from './StageCtaButtons.vue';
import StageImage from './StageImage.vue';
import StagePrice from './StagePrice.vue';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import FeatureStageSlider from './FeatureSlider/FeatureStageSlider.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';
import { setBrandTheme } from '@owc-utils/Theming/theming';

export default (ComponentInitializeDispatcher as StageComponent).extend({
  name: 'owc-stage',
  components: {
    StageCtaButtons,
    StageImage,
    StagePrice,
    FeatureStageSlider,
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent, Personalisation],
  //  supports skeletons layout shifts of the web component
  mounted() {
    if (this.isBuildWc) {
      const host = (this.$el?.getRootNode() as ShadowRoot).host;
      const hostClass = this.isSimpleImageStage ? 'owc-image-stage-host' : 'owc-text-stage-host';
      host.classList.add(hostClass);
    }
  },
  computed: {
    // Makes sure that all API fields needed for the simple image stage mode are filled
    isSimpleImageStage: function() {
      return Boolean(this.personaliseableData?.stageImage);
    },
    hasCtaButtons() {
      return Array.isArray(this.personaliseableData?.buttons) && this.personaliseableData?.buttons.length > 0;
    },
    hasFeatureStageItems() {
      const { featuredItems } = this.personaliseableData;
      return featuredItems != null && featuredItems.length > 0;
    },
    retailLogo() {
      return this.personaliseableData?.retailLogo;
    },
    isDarkMode() {
      return Boolean(this.personaliseableData?.darkMode);
    },
    footnotes() {
      return getLocalFootnotes(this.personaliseableData);
    },
    moduleId() {
      return setModuleId(this);
    },
    brand() {
      return setBrandTheme();
    },
  },
});
</script>

<style lang="scss">
// Mode dependent styles as imported
@import 'StageImage';
@import 'StageText';
@import '@owc-styles/utils/rtl-utils';
@import '@owc-styles/utils/rte';

// Mode independent styles
$stageModuleName: 'owc-stage';
$featureStageModuleName: 'owc-feature-stage';

.#{$stageModuleName} {
  color: var(--wb-black);

  background-color: var(--wb-white);

  &__text {
    text-align: center;

    @include breakpoint-from(mq3) {
      text-align: left;
    }
  }
  &__dealer-logo {
    height: 40px;
    margin-bottom: var(--wb-spacing-s);

    @include breakpoint-from(mq4) {
      height: 64px;
    }
  }
  &__description {
    color: var(--wb-grey-20);

    overflow-wrap: anywhere;
  }
  &__pricing {
    text-align: start;
  }
  // Mode independent styles - dark theme specifics
  &--dark {
    color: var(--wb-white);

    background-color: var(--wb-black);

    .#{$stageModuleName}__description {
      color: var(--wb-white);
    }
  }
  &__footnotes {
    padding-bottom: var(--wb-spacing-l);
  }
}
.#{$featureStageModuleName} {
  display: flex;
  flex: 1;

  .owc-stage-cta-buttons {
    flex-direction: row;
    justify-content: flex-end;
    padding: var(--wb-spacing-m) 0 0;

    @include breakpoint-from(mq4) {
      padding: var(--wb-spacing-s) 0 0;
    }

    .owc-stage-cta-buttons__button:first-child {
      @include breakpoint-to(mq4) {
        margin-left: 0;
      }
    }
  }
}

@include rtl-rule('.owc-stage', false) {
  &__text {
    text-align: right;

    @include breakpoint-from(mq3) {
      text-align: right;
    }
  }
}
@include rtl-rule('.owc-feature-stage', false) {
  .owc-stage-cta-buttons {
    justify-content: flex-start;
  }
}
</style>
